import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDT } from "@/lib/format";

const BUCKETS = [
  ["total", "Total Samples"], ["pending_preparation", "Pending Preparation"], ["pending_qc", "Pending QC"],
  ["pending_sales_approval", "Pending Sales Approval"], ["ready_for_dispatch", "Ready for Dispatch"],
  ["in_transit", "In Transit"], ["delivered", "Delivered"],
];
const STATUSES = ["requested", "preparing", "packed", "qc_approved", "pending_sales_approval", "approved_for_dispatch", "dispatched", "in_transit", "out_for_delivery", "delivered", "qc_rejected", "changes_requested", "delivery_failed", "cancelled"];
const CREATE_ROLES = ["admin", "warehouse_manager", "sales"];

const EMPTY = {
  customer_code: "", customer_name: "", contact_person: "", mobile: "", sample_type: "free", remarks: "",
  ship_to: { company: "", contact_person: "", mobile: "", line1: "", line2: "", area: "", city: "", state: "", country: "India", pin: "" },
};

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label>{children}</div>;
}

export default function Samples() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: "", sample_type: "", courier: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  const canCreate = CREATE_ROLES.includes(me?.role);

  const load = useCallback(async () => {
    const params = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    const [s, l] = await Promise.all([
      api.get("/samples/summary"),
      api.get(`/samples${params ? `?${params}` : ""}`),
    ]);
    setSummary(s.data);
    setRows(l.data);
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const pickCustomer = (code) => {
    const c = customers.find((x) => x.code === code);
    if (!c) return;
    const b = c.billing || {};
    setForm({
      ...form, customer_code: code, customer_name: c.name,
      contact_person: b.contact || "", mobile: b.mobile || "",
      ship_to: { company: c.name, contact_person: b.contact || "", mobile: b.mobile || "", line1: b.address || "", line2: "", area: "", city: b.city || "", state: b.state || "", country: "India", pin: b.pin || "" },
    });
  };

  const addItem = (sku) => {
    if (!sku || items.some((i) => i.sku === sku)) return;
    setItems([...items, { sku, quantity: 1 }]);
  };

  const create = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer / company name is required");
    if (!items.length) return toast.error("Add at least one product");
    try {
      const res = await api.post("/samples", { ...form, customer_code: form.customer_code || null, items });
      toast.success(`${res.data.sample_id} created`);
      setOpen(false);
      setForm(EMPTY);
      setItems([]);
      navigate(`/samples/${res.data.sample_id}`);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed to create sample");
    }
  };

  const setShip = (k, v) => setForm({ ...form, ship_to: { ...form.ship_to, [k]: v } });
  const F = Field;

  return (
    <div className="space-y-4" data-testid="samples-page">
      <SectionTitle right={canCreate && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-sample-button"><Plus className="w-4 h-4 mr-1" /> New Sample Request</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sample-create-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">New Sample Request</DialogTitle>
              <DialogDescription className="sr-only">Create a customer sample request with products and ship-to details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <F label="Customer (master — auto-fills details)">
                  <Select value={form.customer_code || "none"} onValueChange={(v) => v === "none" ? setForm({ ...form, customer_code: "" }) : pickCustomer(v)}>
                    <SelectTrigger data-testid="sample-customer-select"><SelectValue placeholder="Manual entry / select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Manual entry —</SelectItem>
                      {customers.map((c) => <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Customer / Company Name *"><Input data-testid="sample-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></F>
                <F label="Contact Person"><Input data-testid="sample-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></F>
                <F label="Mobile"><Input data-testid="sample-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></F>
                <F label="Sample Type">
                  <Select value={form.sample_type} onValueChange={(v) => setForm({ ...form, sample_type: v })}>
                    <SelectTrigger data-testid="sample-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free / Unpaid</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Remarks"><Input data-testid="sample-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></F>
              </div>

              <div className="space-y-2">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border pb-1">Ship-To Details (mandatory before dispatch)</p>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Company *"><Input data-testid="ship-company" value={form.ship_to.company} onChange={(e) => setShip("company", e.target.value)} /></F>
                  <F label="Contact Person *"><Input data-testid="ship-contact" value={form.ship_to.contact_person} onChange={(e) => setShip("contact_person", e.target.value)} /></F>
                  <F label="Mobile *"><Input data-testid="ship-mobile" value={form.ship_to.mobile} onChange={(e) => setShip("mobile", e.target.value)} /></F>
                  <F label="Address Line 1 *"><Input data-testid="ship-line1" value={form.ship_to.line1} onChange={(e) => setShip("line1", e.target.value)} /></F>
                  <F label="Address Line 2"><Input data-testid="ship-line2" value={form.ship_to.line2} onChange={(e) => setShip("line2", e.target.value)} /></F>
                  <F label="Area / Locality"><Input data-testid="ship-area" value={form.ship_to.area} onChange={(e) => setShip("area", e.target.value)} /></F>
                  <F label="City *"><Input data-testid="ship-city" value={form.ship_to.city} onChange={(e) => setShip("city", e.target.value)} /></F>
                  <F label="State *"><Input data-testid="ship-state" value={form.ship_to.state} onChange={(e) => setShip("state", e.target.value)} /></F>
                  <F label="Country *"><Input data-testid="ship-country" value={form.ship_to.country} onChange={(e) => setShip("country", e.target.value)} /></F>
                  <F label="PIN Code *"><Input data-testid="ship-pin" value={form.ship_to.pin} onChange={(e) => setShip("pin", e.target.value)} /></F>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border pb-1">Products</p>
                <Select value="" onValueChange={addItem}>
                  <SelectTrigger data-testid="sample-product-select"><SelectValue placeholder="Add product from SKU master — specs auto-fill" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.sku} value={p.sku}>{p.sku} — {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {items.map((it, i) => {
                  const p = products.find((x) => x.sku === it.sku);
                  return (
                    <div key={it.sku} className="flex items-center gap-3 border border-border rounded-md p-2" data-testid={`sample-item-${i}`}>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{p?.name || it.sku}</p>
                        <p className="text-[10px] font-mono text-muted-foreground">{it.sku}{p?.specs?.colour ? ` · ${p.specs.colour}` : ""}{p?.specs?.bottle_weight_g ? ` · ${p.specs.bottle_weight_g}g` : ""}{p?.specs?.neck_size_mm ? ` · ${p.specs.neck_size_mm}mm` : ""}</p>
                      </div>
                      <Input type="number" min="1" className="w-24 h-8" data-testid={`sample-item-qty-${i}`} value={it.quantity}
                        onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, quantity: parseInt(e.target.value) || 0 } : x))} />
                      <Button size="sm" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`sample-item-remove-${i}`}><Trash2 className="w-3.5 h-3.5 text-signal-red" /></Button>
                    </div>
                  );
                })}
              </div>

              <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11" onClick={create} data-testid="sample-create-submit">Create Sample Request</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}>Samples</SectionTitle>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="sample-summary">
        {BUCKETS.map(([k, label]) => (
          <div key={k} className="bg-card border border-border rounded-lg p-3" data-testid={`summary-${k}`}>
            <p className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
            <p className="metric text-xl font-extrabold mt-0.5">{summary[k] ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Input placeholder="Search sample no / customer / salesperson" className="w-64 h-9" data-testid="sample-filter-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-48 h-9" data-testid="sample-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.sample_type || "all"} onValueChange={(v) => setFilters({ ...filters, sample_type: v === "all" ? "" : v })}>
          <SelectTrigger className="w-36 h-9" data-testid="sample-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Paid + Free</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="free">Free / Unpaid</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Courier" className="w-36 h-9" data-testid="sample-filter-courier" value={filters.courier} onChange={(e) => setFilters({ ...filters, courier: e.target.value })} />
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="samples-table">
          <thead><tr className="border-b border-border text-left">
            {["Sample No.", "Customer", "Product(s)", "Qty", "Type", "Packed By", "QC", "Sales Approval", "Courier", "Status"].map((h) => (
              <th key={h} className="px-3 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.sample_id} className="border-b border-border/50 cursor-pointer hover:bg-muted/30" onClick={() => navigate(`/samples/${s.sample_id}`)} data-testid={`sample-row-${s.sample_id}`}>
                <td className="px-3 py-2.5 font-mono text-xs text-primary font-bold">{s.sample_id}</td>
                <td className="px-3 py-2.5">{s.customer_name}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-48 truncate">{s.items.map((i) => i.product_name).join(", ")}</td>
                <td className="px-3 py-2.5 metric text-xs">{s.items.reduce((a, i) => a + i.quantity, 0)}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${s.sample_type === "paid" ? "bg-signal-amber/15 text-signal-amber" : "bg-signal-green/15 text-signal-green"}`}>{s.sample_type === "paid" ? "Paid" : "Free"}</span></td>
                <td className="px-3 py-2.5 text-xs">{s.packing?.by || "—"}</td>
                <td className="px-3 py-2.5 text-xs">{s.qc ? (s.qc.status === "approved" ? <span className="text-signal-green">Approved</span> : <span className="text-signal-red">Rejected</span>) : "—"}</td>
                <td className="px-3 py-2.5 text-xs">{s.sales_approval ? (s.sales_approval.status === "approved" ? <span className="text-signal-green">Approved</span> : <span className="text-signal-amber">Changes</span>) : "—"}</td>
                <td className="px-3 py-2.5 text-xs">{s.dispatch?.courier || "—"}</td>
                <td className="px-3 py-2.5"><StatusBadge status={s.status} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-muted-foreground">No samples found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
